import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const CART_STORAGE_KEY = "asklawly_cart_items";

const CartContext = createContext(null);

const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const makeCartItemId = (item = {}) => {
  const product = String(item.product || item.plan || "").trim().toLowerCase();
  const pageTier = item.page_tier || item.pageTier || "";
  const qty = item.qty || "";
  const minutes = item.minutes || "";
  const priceCents = item.price_cents || item.priceCents || item.amount || "";

  return [
    product,
    pageTier ? `pages_${pageTier}` : "",
    qty ? `qty_${qty}` : "",
    minutes ? `minutes_${minutes}` : "",
    priceCents ? `price_${priceCents}` : "",
  ]
    .filter(Boolean)
    .join("__");
};

const normalizeCartItem = (item = {}) => {
  const product = String(item.product || item.plan || "").trim().toLowerCase();
  const pageTier = item.page_tier ?? item.pageTier ?? null;
  const qty = item.qty ?? null;
  const minutes = item.minutes ?? null;

  const priceCents = Math.max(
    0,
    Math.trunc(
      toNumber(
        item.price_cents ??
          item.priceCents ??
          item.amount_cents ??
          item.amount ??
          0,
        0
      )
    )
  );

  const normalized = {
    id: item.id || makeCartItemId({ ...item, product, page_tier: pageTier, qty, minutes, price_cents: priceCents }),
    product,
    plan: product,

    label: item.label || item.title || "Cart item",
    title: item.title || item.label || "Cart item",
    description: item.description || item.desc || "",

    page_tier: pageTier,
    pageTier,

    qty,
    minutes,

    price_cents: priceCents,
    priceCents,

    kind: item.kind || "addon",
    added_at: item.added_at || new Date().toISOString(),
  };

  return normalized;
};

const loadStoredCartItems = () => {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((item) => normalizeCartItem(item))
      .filter((item) => item.product && item.price_cents >= 0);
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => loadStoredCartItems());

  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch {
      // Ignore localStorage write failures.
    }
  }, [cartItems]);

  const addToCart = useCallback((item) => {
    const normalized = normalizeCartItem(item);

    if (!normalized.product) {
      return { ok: false, reason: "Missing product." };
    }

    setCartItems((prev) => {
      const exists = prev.some((existing) => existing.id === normalized.id);

      // Avoid duplicate identical add-ons. User can remove and re-add if needed.
      if (exists) return prev;

      return [...prev, normalized];
    });

    return { ok: true, item: normalized };
  }, []);

  const removeFromCart = useCallback((itemId) => {
    const id = String(itemId || "").trim();
    if (!id) return;

    setCartItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const isInCart = useCallback(
    (itemOrId) => {
      const id =
        typeof itemOrId === "string"
          ? itemOrId
          : normalizeCartItem(itemOrId || {}).id;

      return cartItems.some((item) => item.id === id);
    },
    [cartItems]
  );

  const cartCount = cartItems.length;

  const cartSubtotalCents = useMemo(() => {
    return cartItems.reduce((sum, item) => {
      return sum + Math.max(0, Math.trunc(toNumber(item.price_cents, 0)));
    }, 0);
  }, [cartItems]);

  const value = useMemo(
    () => ({
      cartItems,
      cartCount,
      cartSubtotalCents,

      addToCart,
      removeFromCart,
      clearCart,
      isInCart,

      storageKey: CART_STORAGE_KEY,
    }),
    [
      cartItems,
      cartCount,
      cartSubtotalCents,
      addToCart,
      removeFromCart,
      clearCart,
      isInCart,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);

  if (!ctx) {
    throw new Error("useCart must be used inside CartProvider.");
  }

  return ctx;
};

export default CartContext;
